import * as React from 'react'
import { useRouter } from 'next/router'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { isEqual } from 'lodash'
import Widgets from '../schema/widgets'
import * as Utils from '../utils'
import * as Resolver from '../resolver'
import * as Root from './root'

export function Provider({ children }) {
    const { locale } = useRouter()
    const schema = Root.useDataSchema()
    const service = Root.useServiceObject()
    const send = Root.useService()[1]
    const resolve = React.useMemo(() => {
        return Resolver.create(schema, locale)
    }, [schema, locale])
    const form = useForm({
        defaultValues: Root.useCurrentValues(),
        resolver(values, context) {
            return resolve(Utils.sanitize(values), context)
        },
    })
    const context = React.useMemo(() => ({ widgets: Widgets }), [])
    const { reset } = form

    React.useEffect(() => {
        send({
            type: 'MERGE_DEFAULT_VALUES',
            payload: {
                values: Resolver.createDefaults(schema),
            },
        })
    }, [send, schema])

    // Little hack to "reset" form values and to avoid passing the "reset" function to the event payload
    React.useEffect(() => {
        function listener({ event }) {
            const { type, payload } = event

            if (type === 'RESET_FORM') {
                reset(payload.values)
            }
        }

        return service.subscribe(listener).unsubscribe
    }, [reset, service])

    return (
        <Context.Provider value={context}>
            <FormProvider {...form}>
                {children}
                <StoreService resolve={resolve} />
            </FormProvider>
        </Context.Provider>
    )
}

// Context
const Context = React.createContext()

// Utils components
function StoreService({ resolve }) {
    // "useWatch" inside that componnent localized the rerenderes to that component only
    const watched = useWatch(OBJECT)
    const service = Root.useService()
    const send = service[1]
    const [data, setData] = React.useState(watched)

    React.useEffect(() => {
        // Debounce set data
        const handler = setTimeout(() => {
            setData(previous => {
                if (isEqual(previous, watched)) {
                    return previous
                }

                return watched
            })
        }, 500)

        return () => {
            clearTimeout(handler)
        }
    }, [watched])

    React.useEffect(() => {
        const values = Utils.sanitize(data)

        resolve(values)

        send({
            type: 'CHANGED',
            payload: { values },
        })
    }, [data, resolve, send])

    return null
}
const OBJECT = {}

// Hooks
export function useWidgets() {
    const { widgets } = React.useContext(Context)

    return widgets
}
